//文字コードはutf-8

import { LOADER, ANIMATION } from '../utils/constants.js';

/**
 * ヘッダーの背景色切り替え
 * スクロール位置に応じて、背景の明るさを検知し、
 * ヘッダーの色を白/黒に切り替える
 */
export function initHeaderColorSwitch() {
  const header = document.querySelector('.header');
  
  if (!header) {
    return;
  }
  
  // 白背景ページ（jsHeaderBlackクラスがあるmain要素）ではis-blackクラスを追加
  const main = document.querySelector('main.jsHeaderBlack');
  if (main) {
    header.classList.add('is-black');
    return; // 自動検知を無効化
  }
  
  // modKvが存在する場合、初期状態で白いヘッダーにする
  const modKv = document.querySelector('.modKv');
  if (modKv) {
    header.classList.remove('is-black');
  }
  
  // jsHeaderBlackクラスを持つセクション要素を取得
  const headerBlackSections = document.querySelectorAll('.jsHeaderBlack');
  
  /**
   * RGB値から明るさを計算する（0-255）
   * @param {string} color - rgb()形式の色文字列
   * @returns {number} - 明るさの値（0-255）
   */
  function getBrightness(color) {
    // rgb(r, g, b) または rgba(r, g, b, a) から数値を抽出
    const match = color.match(/\d+/g);
    if (!match || match.length < 3) {
      return 0;
    }
    
    const r = parseInt(match[0]);
    const g = parseInt(match[1]);
    const b = parseInt(match[2]);
    
    // 相対輝度の計算（人間の目の感度を考慮）
    return (r * 0.299 + g * 0.587 + b * 0.114);
  }
  
  /**
   * ヘッダー位置の背景色を取得して明るさを判定する
   */
  function updateHeaderColor() {
    // ローディング画面が表示中の場合は処理をスキップ
    const loader = document.querySelector(LOADER.SELECTOR);
    if (loader && !loader.classList.contains('is-fadeout') && loader.style.display !== 'none') {
      return;
    }
    
    // ヘッダーの中心位置を取得
    const headerRect = header.getBoundingClientRect();
    const headerCenterY = headerRect.top + headerRect.height / 2;
    const headerCenterX = window.innerWidth / 2;
    
    // topKvエリアの判定を最優先（ページ上部では黒ヘッダーにする）
    const topKv = document.querySelector('.topKv');
    if (topKv) {
      const topKvRect = topKv.getBoundingClientRect();
      // ヘッダーの中心がtopKvエリアの範囲内にある場合
      if (headerCenterY >= topKvRect.top && headerCenterY <= topKvRect.bottom) {
        header.classList.add('is-black');
        return;
      }
    }
    
    // modKvエリアの判定を最優先（ページ上部では白ヘッダーにする）
    const modKv = document.querySelector('.modKv');
    if (modKv) {
      const modKvRect = modKv.getBoundingClientRect();
      // ヘッダーの中心がmodKvエリアの範囲内にある場合
      if (headerCenterY >= modKvRect.top && headerCenterY <= modKvRect.bottom) {
        header.classList.remove('is-black');
        return;
      }
    }
    
    // jsHeaderBlackクラスを持つセクションがヘッダー位置にあるかチェック
    // （topKvエリア内の場合は既にreturnしているため、ここには到達しない）
    if (headerBlackSections.length > 0) {
      for (const section of headerBlackSections) {
        const sectionRect = section.getBoundingClientRect();
        // ヘッダーの中心がセクションの範囲内にある場合
        if (headerCenterY >= sectionRect.top && headerCenterY <= sectionRect.bottom) {
          header.classList.add('is-black');
          return;
        }
      }
    }
    
    // ヘッダーの下にある要素を取得（ヘッダー自体は除外）
    header.style.pointerEvents = 'none';
    const elementBehind = document.elementFromPoint(headerCenterX, headerCenterY);
    header.style.pointerEvents = '';
    
    if (!elementBehind) {
      return;
    }
    
    // video要素やimg要素の場合は暗い背景として扱う
    if (elementBehind.tagName === 'VIDEO' || elementBehind.tagName === 'IMG') {
      header.classList.remove('is-black');
      return;
    }
    
    // 背景色を取得（要素自身または親要素を遡って取得）
    let currentElement = elementBehind;
    let backgroundColor = 'rgba(0, 0, 0, 0)';
    let depth = 0;
    const maxDepth = 20; // 最大探索深度
    
    while (currentElement && depth < maxDepth) {
      const style = window.getComputedStyle(currentElement);
      const bgColor = style.backgroundColor;
      
      // 特定のクラスを持つ要素（動画・画像コンテナ、暗い背景セクション）の場合は暗い背景として扱う
      const className = currentElement.className || '';
      if (className.includes('topKv__video') || 
          className.includes('topKv__image') || 
          className.includes('topKv') ||
          className.includes('modKv') ||
          className.includes('topParallax__img') ||
          className.includes('topParallax') ||
          className.includes('topCourse--course') ||
          className.includes('topCourse--childCourse') ||
          className.includes('topSection--news')) {
        header.classList.remove('is-black');
        return;
      }
      
      // 透明でない背景色が見つかった場合
      if (bgColor && bgColor !== 'rgba(0, 0, 0, 0)' && bgColor !== 'transparent') {
        backgroundColor = bgColor;
        break;
      }
      
      // 背景画像がある場合は暗い背景と判定
      if (style.backgroundImage && style.backgroundImage !== 'none') {
        backgroundColor = 'rgb(50, 50, 50)';
        break;
      }
      
      currentElement = currentElement.parentElement;
      depth++;
    }
    
    // 明るさを判定（閾値: 220 - より明るい背景のみis-blackを付ける）
    const brightness = getBrightness(backgroundColor);
    
    if (brightness > 220) {
      // 明るい背景：黒いヘッダー
      header.classList.add('is-black');
    } else {
      // 暗い背景：白いヘッダー
      header.classList.remove('is-black');
    }
  }
  
  // ローディング画面のフェードアウト完了を監視
  window.addEventListener('loaderFadeOut', function() {
    // ローディング完了後、ヘッダーの色を再判定
    updateHeaderColor();
  });
  
  // 初回実行（DOMContentLoaded後に少し遅延させて実行）
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      setTimeout(updateHeaderColor, ANIMATION.HEADER_COLOR_DELAY);
    });
  } else {
    setTimeout(updateHeaderColor, ANIMATION.HEADER_COLOR_DELAY);
  }
  
  // スクロール時に実行（パフォーマンスのためrequestAnimationFrameを使用）
  let ticking = false;
  
  window.addEventListener('scroll', function() {
    if (!ticking) {
      window.requestAnimationFrame(function() {
        updateHeaderColor();
        ticking = false;
      });
      ticking = true;
    }
  });
  
  // リサイズ時にも実行
  let resizeTimeout;
  window.addEventListener('resize', function() {
    clearTimeout(resizeTimeout);
    resizeTimeout = setTimeout(updateHeaderColor, ANIMATION.HEADER_COLOR_DELAY);
  });
}

